/** ***********
*
* <manage-right-request.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { RightRequest } from 'src/app/models/user-right';
import { UserRightService } from 'src/app/services/user-right.service';
import { TranslateService } from '@ngx-translate/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import { selectRequestsLoaded$, selectRightRequests$ } from 'src/app/store/selectors/user-right.selectors';
import { SuccessRightRequest } from 'src/app/store/actions/user-right.action';
import { detailExpand, rotate } from '../../shared/animations';

@Component({
    selector: 'app-manage-right-request',
    templateUrl: './manage-right-request.component.html',
    styleUrls: ['./manage-right-request.component.scss'],
    animations: [detailExpand, rotate(180)],
    standalone: false
})

export class ManageRightRequestComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild('table', { read: ElementRef }) table: ElementRef<HTMLTableElement>;

    /**
     * Loader and no data template
     */
    public isLoadingTable: boolean;
    public isNoData: boolean;

    /**
     * Table data
     */
    public rightRequests: RightRequest[] = [];
    public dataSource = new MatTableDataSource();
    public displayedColumns = ['date', 'user', 'groupName', 'level', 'parentFamilyName', 'familyName', 'actionColumns'];
    public expandedColumns: Array<string> = ['note'];
    public columnNames = {
        date: this.translate.instant('DATE'),
        user: this.translate.instant('USER'),
        userDepartment: this.translate.instant('DEPARTMENT'),
        familyName: this.translate.instant('ASSET_FAMILY'),
        parentFamilyName: this.translate.instant('PARENT_FAMILY'),
        level: this.translate.instant('RIGHT_LEVEL'),
        status: this.translate.instant('REQUEST_RIGHT_STATUS'),
        actionColumns: this.translate.instant('ACTIONS'),
        groupName: this.translate.instant('GROUP')
    };
    private _expandedRows: RightRequest[] = [];
    private _subscription = new Subscription();

    constructor(
        private userRightService: UserRightService,
        private translate: TranslateService,
        private cd: ChangeDetectorRef,
        private store: Store,
    ) { }

    ngOnInit() {
        this.isLoadingTable = true;
        this.cd.markForCheck();

        /* Refresh data on init */
        this._subscription.add(
            this.userRightService.getPendingRequests().subscribe((requests) => {
                this.store.dispatch(new SuccessRightRequest(requests));
            })
        );
    }

    ngAfterViewInit(): void {
        // subscribe to right requests list once the data is loaded
        const loadSubscription = new Subscription();
        loadSubscription.add(
            this.store.select(selectRequestsLoaded$).subscribe((loaded) => {
                if (loaded) {
                    loadSubscription.unsubscribe();

                    this._subscription.add(
                        this.store.select(selectRightRequests$).subscribe(
                            (rightRequests) => {
                                this.rightRequests = rightRequests.filter(request => request.managerUserId.toLowerCase() === this.userRightService.getUserNgId().toLowerCase());
                                // Remove the expanded rows which are not in the data
                                this._expandedRows = this._expandedRows.filter(
                                    ({ id }) => !!this.rightRequests.find(request => request.id === id)
                                );
                                this.dataSource.data = this.rightRequests.map((element):  RightRequest => {
                                    const data:  RightRequest = element;
                                    const dataSourceRightRequest = this.dataSource.data.find(
                                        ({ id }) => id === data.id
                                      ) ?? {};
                                      // Keep reference (avoid the collapse of the item when the datSource is set)
                                      return Object.assign(dataSourceRightRequest, data);

                                });
                                this.dataSource.sort = this.sort;
                                this.dataSource.paginator = this.paginator;
                                this._subscription.add(this.dataSource.connect().subscribe((data) => {
                                    this.isLoadingTable = false;
                                    this.isNoData = data.length === 0 ? true : false;
                                    this.cd.markForCheck();
                                }));
                            }
                        )
                    );
                }
            })
        );
    }

    /**
     * Fired when the manager accepts a demand
     */
    public onClickAcceptDemand(request: RightRequest) {
        request.isLoading = true;
        this.userRightService.acceptDemand(request).subscribe(
            () => {
                request.isLoading = false;
            });
    }

    /**
     * Fired when the manager refuses a demand
     */
    public onClickRefuseDemand(request: RightRequest) {
        request.isLoading = true;
        this.userRightService.refuseDemand(request).subscribe(
            () => {
                request.isLoading = false;
            }
        );
    }

    /**
     * Called when the user change the paginator page
     * - Aims to auto scroll to the top of the table
     */
    public scrollUp() {
        this.table.nativeElement.scrollIntoView({ behavior: 'smooth' });
        window.scrollTo(0, 0);
    }

    /**
     * Get the translated name of table column header
     * @param value - The name of the column to be translated
     */
    public getRealName(value: string): string {
        return this.columnNames[value];
    }

    /**
     * Handle custom sorting
     */
    public sortData(sort: Sort) {
        const sortedData = this.dataSource.data.slice();
        if (!sort.active || sort.direction === '') {
            return;
        }

        this.dataSource.data = sortedData.sort((a: RightRequest, b: RightRequest) => {
            const isAsc = sort.direction === 'asc';
            if (sort.active === 'user') {
                return this._compare(a.requesterLastname, b.requesterLastname, isAsc);
            } else {
                return 0;
            }
        });
    }

    /**
     * Used to handle the custom sorting
     */
    private _compare(a: number | string, b: number | string, isAsc: boolean) {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    /**
     * Get if the row has been expanded
     * @param row The row of the table
     * @returns The row is expanded
     */
    public isRowExpanded(row: RightRequest): boolean {
        if (row.note) {
            return !!this._expandedRows.find(
                ({ id }) => row.id === id
            );
        }
    }

    /**
     * Stores the expansion state of the row
     * @param row The tracker
     */
    public switchRowExpansion(row: RightRequest): void {
        const index = this._expandedRows.findIndex(
            request => request.id === row.id
        );
        if (index > -1) {
            this._expandedRows.splice(index, 1);
        } else {
            this._expandedRows.push(row);
        }
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
}
